import { useEffect, useState } from 'react';

type Integration = {
  id: string;
  name: string;
  desc: string;
  icon: string;
  active: boolean;
  envKey: string;
  docs: string;
  capabilities: readonly string[];
};

const INTEGRATIONS: readonly Integration[] = [
  {
    id: 'openai',
    name: 'OpenAI',
    desc: 'Moderation, Sentiment-Analyse, Carlos-Chat — gpt-4o-mini + omni-moderation.',
    icon: 'auto_awesome',
    active: true,
    envKey: 'OPENAI_API_KEY',
    docs: 'https://platform.openai.com/docs',
    capabilities: ['Review-Moderation', 'Carlos-Chat', 'Übersetzung (Fallback)'],
  },
  {
    id: 'deepl',
    name: 'DeepL',
    desc: 'Übersetzung von Reviews zwischen DE / IT / FR / EN.',
    icon: 'translate',
    active: false,
    envKey: 'DEEPL_API_KEY',
    docs: 'https://developers.deepl.com',
    capabilities: ['Inline-Übersetzung im Drawer', 'Bulk-Übersetzung Pipelines'],
  },
  {
    id: 'bloomreach',
    name: 'Bloomreach',
    desc: 'Versand der Review-Request-E-Mails über Bloomreach Engagement.',
    icon: 'send',
    active: false,
    envKey: 'BLOOMREACH_API_KEY',
    docs: 'https://documentation.bloomreach.com',
    capabilities: ['Transactional E-Mail', 'Template-Personalisierung', 'Bounce-Handling'],
  },
  {
    id: 'scayle',
    name: 'SCAYLE',
    desc: 'Order- und Produktdaten-Sync aus dem Headless-Commerce-Backend.',
    icon: 'shopping_cart',
    active: false,
    envKey: 'SCAYLE_API_KEY',
    docs: 'https://developer.scayle.com',
    capabilities: ['Order-Webhook', 'Produkt-Stammdaten-Sync', 'Customer-Lookup'],
  },
];

export function IntegrationsPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const open = openId ? INTEGRATIONS.find((i) => i.id === openId) ?? null : null;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h2 className="page-title">Integrations</h2>
          <p className="page-sub">
            Externe Schnittstellen — Übersetzung, KI, E-Mail-Versand und Order-Sync.
          </p>
        </div>
      </div>

      <div className="tile-grid">
        {INTEGRATIONS.map((it) => (
          <button
            type="button"
            className="tile"
            key={it.id}
            onClick={() => setOpenId(it.id)}
            style={{ textAlign: 'left' }}
          >
            <div className="tile-icon">
              <span className="material-symbols-rounded">{it.icon}</span>
            </div>
            <div className="tile-name">{it.name}</div>
            <div className="tile-desc">{it.desc}</div>
            <div className={`tile-status ${it.active ? 'on' : 'off'}`}>
              <span className="dot" aria-hidden="true" />
              {it.active ? 'Aktiv' : 'Nicht konfiguriert'}
            </div>
          </button>
        ))}
      </div>

      {open && <IntegrationDrawer integration={open} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function IntegrationDrawer({ integration, onClose }: { integration: Integration; onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <aside className="drawer" role="dialog" aria-label={`Integration ${integration.name}`}>
        <header className="drawer-head">
          <h2>{integration.name}</h2>
          <span className={`badge ${integration.active ? 'published' : 'hidden'}`}>
            {integration.active ? 'Aktiv' : 'Nicht konfiguriert'}
          </span>
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Schließen">
            <span className="material-symbols-rounded">close</span>
          </button>
        </header>
        <div className="drawer-body">
          <div className="d-section">
            <div className="d-label">Beschreibung</div>
            <p style={{ font: '400 14px/22px var(--font-body)', color: 'var(--fg-1)', margin: 0 }}>
              {integration.desc}
            </p>
          </div>

          <div className="d-section">
            <div className="d-label">Verwendet für</div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
              {integration.capabilities.map((c) => (
                <li key={c} style={{ display: 'flex', gap: 8, font: '500 13px/18px var(--font-body)', color: 'var(--fg-1)' }}>
                  <span className="material-symbols-rounded" style={{ fontSize: 16, color: 'var(--brand-green)' }}>
                    check_circle
                  </span>
                  {c}
                </li>
              ))}
            </ul>
          </div>

          <div className="d-section">
            <div className="d-label">API-Key</div>
            <div className="d-product">
              <div>
                <div className="mono">{integration.envKey}</div>
                <div className="sku">
                  Wert wird in den Nhost Project Settings → Secrets gepflegt; nicht in der UI sichtbar.
                </div>
              </div>
              <span className={`badge ${integration.active ? 'published' : 'hidden'}`}>
                {integration.active ? 'gesetzt' : 'leer'}
              </span>
            </div>
          </div>

          <div className="d-section">
            <div className="d-label">Doku</div>
            <a
              href={integration.docs}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                font: '500 13px/18px var(--font-body)',
                color: 'var(--brand-blue)',
              }}
            >
              {integration.docs}
              <span className="material-symbols-rounded" style={{ fontSize: 14 }}>open_in_new</span>
            </a>
          </div>
        </div>
        <footer className="drawer-foot">
          <button type="button" className="btn primary" disabled>
            <span className="material-symbols-rounded">key</span>
            API-Key setzen
          </button>
          <button type="button" className="btn">
            <span className="material-symbols-rounded">play_circle</span>
            Verbindung testen
          </button>
          <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>
            Schließen
          </button>
        </footer>
      </aside>
    </>
  );
}
